import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle } from "@ionic/react";
import { useMemo, useState } from "react";
import { useData } from "../../services/DataProvider";

const BackfillRemoveImpliedQuizQuestionLinks: React.FC = () => {
  const { quizzes, questions, adminCreateRecord } = useData();
  const [running, setRunning] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  const impliedStandardsPlan = useMemo(() => {
    const questionById = new Map<string, typeof questions[number]>();
    questions.forEach((question) => {
      if (question.id) {
        questionById.set(question.id, question);
      }
    });
    const quizById = new Map<string, typeof quizzes[number]>();
    quizzes.forEach((quiz) => {
      if (quiz.id) {
        quizById.set(quiz.id, quiz);
      }
    });

    let totalQuizQuestionLinks = 0;
    let totalQuestionQuizLinks = 0;
    let impliedQuizLinks = 0;
    let impliedQuestionLinks = 0;
    let missingLinks = 0;

    const quizRemovals = new Map<string, Set<string>>();
    const questionRemovals = new Set<string>();

    quizzes.forEach((quiz) => {
      const quizId = quiz.id;
      if (!quizId) {
        return;
      }
      const quizQuestionIds = quiz.questionIDs || [];
      totalQuizQuestionLinks += quizQuestionIds.length;
      if (!quizQuestionIds.length || !quiz.standardIDs?.length) {
        return;
      }
      const standardSet = new Set(quiz.standardIDs);
      quizQuestionIds.forEach((questionId) => {
        const question = questionById.get(questionId);
        if (!question) {
          missingLinks += 1;
          return;
        }
        const hasStandard = (question.standardsCodes || []).some((code) => standardSet.has(code));
        if (!hasStandard) {
          return;
        }
        impliedQuizLinks += 1;
        const removalSet = quizRemovals.get(quizId) || new Set<string>();
        removalSet.add(questionId);
        quizRemovals.set(quizId, removalSet);
      });
    });

    questions.forEach((question) => {
      if (!question.quizID) {
        return;
      }
      totalQuestionQuizLinks += 1;
      const quiz = quizById.get(question.quizID);
      if (!quiz) {
        missingLinks += 1;
        return;
      }
      if (!quiz.standardIDs?.length) {
        return;
      }
      const standardSet = new Set(quiz.standardIDs);
      const hasStandard = (question.standardsCodes || []).some((code) => standardSet.has(code));
      if (!hasStandard) {
        return;
      }
      impliedQuestionLinks += 1;
      questionRemovals.add(question.id);
    });

    return {
      quizRemovals,
      questionRemovals,
      totalQuizQuestionLinks,
      totalQuestionQuizLinks,
      impliedQuizLinks,
      impliedQuestionLinks,
      missingLinks,
      quizzesToUpdate: Array.from(quizRemovals.keys()).length,
      questionsToUpdate: questionRemovals.size,
    };
  }, [quizzes, questions]);

  const runImpliedStandardsBackfill = async () => {
    if (running) {
      return;
    }
    setRunning(true);
    setResultMessage("");
    try {
      await Promise.all(
        Array.from(impliedStandardsPlan.quizRemovals.entries()).map(async ([quizId, removalIds]) => {
          const quiz = quizzes.find((entry) => entry.id === quizId);
          if (!quiz || !quiz.questionIDs?.length) {
            return;
          }
          const nextQuestionIds = quiz.questionIDs.filter((questionId) => !removalIds.has(questionId));
          if (nextQuestionIds.length === quiz.questionIDs.length) {
            return;
          }
          await adminCreateRecord("finlitri-quizzes", {
            ...quiz,
            questionIDs: nextQuestionIds,
          });
        })
      );

      await Promise.all(
        Array.from(impliedStandardsPlan.questionRemovals.values()).map(async (questionId) => {
          const question = questions.find((entry) => entry.id === questionId);
          if (!question) {
            return;
          }
          await adminCreateRecord("finlitri-questions", {
            ...question,
            quizID: "",
          });
        })
      );

      setResultMessage("Implied link cleanup completed successfully.");
    } catch (error) {
      setResultMessage(`Implied link cleanup failed: ${String(error)}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Remove Implied Quiz Question Links</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <p>
          This backfill removes quiz/question links that are already implied by standards
          (question.standardsCodes intersects quiz.standardIDs), on both the quiz and question side.
        </p>
        <p><b>Quiz → Question refs (quiz.questionIDs): </b> {impliedStandardsPlan.totalQuizQuestionLinks}</p>
        <p><b>Question → Quiz refs (question.quizID): </b> {impliedStandardsPlan.totalQuestionQuizLinks}</p>
        <p><b>Implied quiz links to remove: </b> {impliedStandardsPlan.impliedQuizLinks}</p>
        <p><b>Implied question links to remove: </b> {impliedStandardsPlan.impliedQuestionLinks}</p>
        <p><b>Quizzes to update: </b> {impliedStandardsPlan.quizzesToUpdate}</p>
        <p><b>Questions to update: </b> {impliedStandardsPlan.questionsToUpdate}</p>
        <p><b>Missing docs referenced by links: </b> {impliedStandardsPlan.missingLinks}</p>

        <IonButton
          expand="block"
          onClick={runImpliedStandardsBackfill}
          disabled={running || (impliedStandardsPlan.impliedQuizLinks === 0 && impliedStandardsPlan.impliedQuestionLinks === 0)}
          aria-label="Remove implied quiz question links"
          title="Remove implied quiz question links"
        >
          {running ? "Running..." : "Run Implied Link Cleanup"}
        </IonButton>
        {resultMessage && <p>{resultMessage}</p>}
      </IonCardContent>
    </IonCard>
  );
};

export default BackfillRemoveImpliedQuizQuestionLinks;
